import type { Company } from '@gat-ship/types'
import * as CompanyEntity from './company-entity'
import { findCompany } from './company-collection'

/**
 * Functional core — command *decisions*.
 *
 * A decider answers "given the current companies and this input, what should
 * happen?" as data: either the next entity to write (`ok: true`) or the reason
 * the command can't proceed (`ok: false`). It performs no I/O, touches no ref,
 * and toggles no loading state — the imperative shell turns this decision into
 * effects (a thrown error or a cache write).
 */
type CompanyDecision =
  | { ok: true; next: Company }
  | { ok: false; reason: string }

/**
 * Shared decision guard: locate the target company or explain why the command
 * can't proceed, then apply a pure transform. Keeps the per-command deciders
 * to a single expression each.
 */
function decideTransform(
  companies: readonly Company[],
  companyCode: string,
  transform: (company: Company) => Company
): CompanyDecision {
  const target = findCompany(companies, companyCode)
  if (!target) {
    return { ok: false, reason: 'Company not found' }
  }
  return { ok: true, next: transform(target) }
}

/** Decide the post-change company for a details edit. Pure. */
export function decideChangeDetails(
  companies: readonly Company[],
  companyCode: string,
  name: string,
  description?: string
) {
  return decideTransform(companies, companyCode, (company) =>
    CompanyEntity.updateDetails(company, name, description)
  )
}

/** Decide the deactivated company. Pure. */
export function decideDeactivate(
  companies: readonly Company[],
  companyCode: string
) {
  return decideTransform(companies, companyCode, (company) =>
    CompanyEntity.deactivate(company)
  )
}

/** Decide the reactivated company. Pure. */
export function decideReactivate(
  companies: readonly Company[],
  companyCode: string
) {
  return decideTransform(companies, companyCode, (company) =>
    CompanyEntity.activate(company)
  )
}
